import type { FilterObject } from 'core-system/Table/FilterModel'
import moment, { Moment } from 'moment'
import { uniq } from 'lodash-es'
import type { ChipVariants } from 'core-system/Chip'
import type {
  ReimbursementStatus,
  ReimbursementSubmission,
} from 'redux/reimbursements/reimbursementsTypes'
const PENDING_STATUSES: ReimbursementStatus[] = ['CREATED']

const PROCESSED_STATUSES: ReimbursementStatus[] = [
  'APPROVED',
  'PARTIAL',
  'REJECTED',
]

const statusLabels: Record<ReimbursementStatus, string> = {
  CREATED: 'Under Review',
  PROCESSED: 'Processed',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  PARTIAL: 'Partial',
}

const statusVariants: Record<ReimbursementStatus, ChipVariants> = {
  CREATED: 'yellow',
  PROCESSED: 'blue',
  SUBMITTED: 'purple',
  APPROVED: 'green',
  REJECTED: 'red',
  PARTIAL: 'blue',
}

export const reimbursementStatusLabel = (status: ReimbursementStatus) =>
  statusLabels[status]

export const reimbursementStatusVariant = (status: ReimbursementStatus) =>
  statusVariants[status]

export const formatExpenseType = (expenseType: string | null) => {
  if (!expenseType) {
    return '—'
  }
  return expenseType.charAt(0) + expenseType.slice(1).toLowerCase()
}

export const formatReimbursementMoney = (cents: number | null | undefined) => {
  if (cents == null) {
    return '—'
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100)
}

export const isPendingReimbursement = (receipt: ReimbursementSubmission) =>
  PENDING_STATUSES.includes(receipt.status)

export const isProcessedReimbursement = (receipt: ReimbursementSubmission) =>
  PROCESSED_STATUSES.includes(receipt.status)

const referenceMoment = (receipt: ReimbursementSubmission) => {
  const value = receipt.resolvedAt || receipt.createdAt || receipt.receiptDatetime
  return value ? moment(value) : null
}

export const isWithinLast12Months = (
  receipt: ReimbursementSubmission,
  now: Moment = moment()
) => {
  const date = referenceMoment(receipt)
  if (!date || !date.isValid()) {
    return false
  }
  return date.isSameOrAfter(now.clone().subtract(12, 'months'))
}

export const approvedDisplayCents = (receipt: ReimbursementSubmission) => {
  if (receipt.approvedAmount != null) {
    return receipt.approvedAmount
  }
  if (receipt.status === 'REJECTED') {
    return 0
  }
  return null
}

export const resolutionCaption = (receipt: ReimbursementSubmission) => {
  if (!receipt.resolvedAt || !isProcessedReimbursement(receipt)) {
    return null
  }
  const resolved = moment(receipt.resolvedAt)
  if (!resolved.isValid()) {
    return null
  }
  return `${reimbursementStatusLabel(receipt.status)} ${resolved.format('MMM D, YYYY')}`
}

const sumAmounts = (
  receipts: ReimbursementSubmission[],
  amount: (receipt: ReimbursementSubmission) => number
) => receipts.reduce((total, receipt) => total + amount(receipt), 0)

const claimNoun = (count: number) => (count === 1 ? 'claim' : 'claims')

export interface ReimbursementSummary {
  pendingAmount: number
  pendingCaption: string
  approvedAmount: number
  approvedCaption: string
  deniedAmount: number
  deniedCaption: string
}

export const summarizeReimbursements = (
  receipts: ReimbursementSubmission[],
  now: Moment = moment()
): ReimbursementSummary => {
  const pending = receipts.filter(isPendingReimbursement)
  const recent = receipts.filter((receipt) => isWithinLast12Months(receipt, now))
  const approved = recent.filter(
    (receipt) => receipt.status === 'APPROVED' || receipt.status === 'PARTIAL'
  )
  const denied = recent.filter((receipt) => receipt.status === 'REJECTED')

  let oldestDays = 0
  if (pending.length) {
    const oldest = pending.reduce((earliest, receipt) => {
      const created = moment(receipt.createdAt)
      return created.isBefore(earliest) ? created : earliest
    }, moment(pending[0].createdAt))
    oldestDays = Math.max(
      0,
      now.clone().startOf('day').diff(oldest.clone().startOf('day'), 'days')
    )
  }

  return {
    pendingAmount: sumAmounts(pending, (receipt) => receipt.amount || 0),
    pendingCaption: pending.length
      ? `${pending.length} ${claimNoun(pending.length)} · oldest waiting ${oldestDays} days`
      : '0 claims',
    approvedAmount: sumAmounts(
      approved,
      (receipt) => receipt.approvedAmount || 0
    ),
    approvedCaption: `${approved.length} ${claimNoun(approved.length)} · last 12 months`,
    deniedAmount: sumAmounts(denied, (receipt) => receipt.amount || 0),
    deniedCaption: `${denied.length} ${claimNoun(denied.length)} · last 12 months`,
  }
}

export const pendingReimbursements = (receipts: ReimbursementSubmission[]) =>
  receipts.filter(isPendingReimbursement)

export const processedReimbursements = (
  receipts: ReimbursementSubmission[],
  now: Moment = moment()
) =>
  receipts.filter(
    (receipt) =>
      isProcessedReimbursement(receipt) && isWithinLast12Months(receipt, now)
  )

const CATEGORY_FILTER_ORDER = [
  'Transit',
  'Parking',
  'Tolls',
  'Rideshare',
  'Gas',
  'Bikeshare',
]

const STATUS_FILTER_ORDER = [
  'Under Review',
  'Approved',
  'Partial',
  'Rejected',
  'Processed',
  'Submitted',
]

export interface ReimbursementTableRow {
  id: string
  tripDate: string
  tripDateKey: string
  category: string
  requested: string
  approved: string
  status: string
  receipt: ReimbursementSubmission
}

export const REIMBURSEMENT_SEARCH_KEYS = [
  'tripDate',
  'category',
  'requested',
  'approved',
  'status',
]

export const buildReimbursementRow = (
  receipt: ReimbursementSubmission
): ReimbursementTableRow => {
  const rawDate = receipt.receiptDatetime || receipt.createdAt || ''
  const parsed = rawDate ? moment(rawDate) : null
  return {
    id: receipt.id,
    tripDate: parsed?.isValid() ? parsed.format('MMM D, YYYY') : '—',
    tripDateKey: rawDate,
    category: formatExpenseType(receipt.expenseType),
    requested: formatReimbursementMoney(receipt.amount),
    approved: formatReimbursementMoney(approvedDisplayCents(receipt)),
    status: reimbursementStatusLabel(receipt.status),
    receipt,
  }
}

const orderedLabels = (labels: string[], preferred: string[]) => {
  const known = preferred.filter((label) => labels.includes(label))
  const extra = uniq(labels).filter((label) => !preferred.includes(label))
  return [...known, ...extra]
}

const checkboxGroup = (header: string, key: string, labels: string[]) => ({
  header,
  checkboxes: labels.map((value) => ({
    label: value,
    checked: false,
    key,
    value,
  })),
})

export const reimbursementFilters = (
  rows: ReimbursementTableRow[]
): FilterObject[] => {
  const categories = orderedLabels(
    rows.map((row) => row.category),
    CATEGORY_FILTER_ORDER
  )
  const statuses = orderedLabels(
    rows.map((row) => row.status),
    STATUS_FILTER_ORDER
  )
  const filters: FilterObject[] = []
  if (categories.length) {
    filters.push(checkboxGroup('Category', 'category', categories))
  }
  if (statuses.length) {
    filters.push(checkboxGroup('Status', 'status', statuses))
  }
  return filters
}
